import { useShow } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import { Card, Tag, Input, Button, List, Avatar, message, Space, Descriptions, Divider } from 'antd';
import { UserOutlined, SendOutlined, CheckOutlined, CarOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../lib/api-client';
import { usePermission } from '../../hooks/usePermission';
import { t } from '../../i18n';

interface Message {
  id: number;
  content: string;
  senderType: 'CLIENT' | 'DRIVER' | 'SUPPORT' | 'SYSTEM';
  createdAt: string;
  sender: {
    fullName: string;
  };
}

interface RelatedTrip {
  id: number;
  tripType: 'INTERCITY_TAXI' | 'INTERREGION_TAXI' | 'POSTAGE' | 'CARGO';
  status: string;
  pickupAddress: string;
  destinationAddress: string;
  finalPrice: number | null;
  priceEstimate: number;
  requestedAt: string;
  client?: {
    fullName: string;
    phone: string;
  };
  driver?: {
    fullName: string;
    phone: string;
  };
}

interface Ticket {
  id: number;
  subject: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
  createdAt: string;
  tripType?: string;
  tripId?: number;
  user: {
    fullName: string;
    phone: string;
  };
  messages: Message[];
  relatedTrip?: RelatedTrip;
}

const tripTypeLabels: Record<string, string> = {
  INTERCITY_TAXI: t('tripType.intercityTaxi'),
  INTERREGION_TAXI: t('tripType.interregionTaxi'),
  POSTAGE: t('tripType.postage'),
  CARGO: t('tripType.cargo'),
};

const tripStatusLabels: Record<string, string> = {
  REQUESTED: t('status.requested'),
  ACCEPTED: t('status.accepted'),
  DRIVER_ARRIVED: t('status.driverArrived'),
  PICKED_UP: t('status.pickedUp'),
  ON_THE_WAY: t('status.onTheWay'),
  IN_PROGRESS: t('status.inProgress'),
  COMPLETED: t('status.completed'),
  CANCELLED: t('status.cancelled'),
};

const tripStatusColors: Record<string, string> = {
  REQUESTED: 'blue',
  ACCEPTED: 'cyan',
  DRIVER_ARRIVED: 'purple',
  PICKED_UP: 'geekblue',
  ON_THE_WAY: 'orange',
  IN_PROGRESS: 'orange',
  COMPLETED: 'green',
  CANCELLED: 'red',
};

export const SupportTicketShow = () => {
  const { can } = usePermission();
  const { queryResult } = useShow<Ticket>();
  const { data, isLoading, refetch } = queryResult;
  const ticket = data?.data;
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  // Auto-refresh every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 15000);
    return () => clearInterval(interval);
  }, [refetch]);

  const statusLabels: Record<string, string> = {
    OPEN: t('support.statusOpen'),
    IN_PROGRESS: t('support.statusInProgress'),
    RESOLVED: t('support.statusResolved'),
    CLOSED: t('support.statusClosed'),
  };

  const statusColors: Record<string, string> = {
    OPEN: 'red',
    IN_PROGRESS: 'orange',
    RESOLVED: 'green',
    CLOSED: 'default',
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;

    setSending(true);
    try {
      await apiClient.post(`/admin/tickets/${ticket?.id}/reply`, {
        content: replyText,
      });
      message.success(t('support.replySuccess'));
      setReplyText('');
      refetch();
    } catch {
      message.error(t('common.error'));
    } finally {
      setSending(false);
    }
  };

  const handleResolve = async () => {
    try {
      await apiClient.put(`/admin/tickets/${ticket?.id}/resolve`);
      message.success(t('support.resolveSuccess'));
      refetch();
    } catch {
      message.error(t('common.error'));
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + ' so\'m';
  };

  const handleViewTrip = () => {
    if (ticket?.tripType && ticket?.tripId) {
      navigate(`/trips/${ticket.tripType}/${ticket.tripId}`);
    }
  };

  return (
    <Show
      isLoading={isLoading}
      title={`${t('support.ticketDetails')} #${ticket?.id}`}
      headerButtons={
        can('support', 'resolve') &&
        ticket?.status !== 'RESOLVED' &&
        ticket?.status !== 'CLOSED' && (
          <Button type="primary" icon={<CheckOutlined />} onClick={handleResolve}>
            Hal qilish
          </Button>
        )
      }
    >
      <Card style={{ marginBottom: 24 }}>
        <Space direction="vertical" size="small">
          <div>
            <strong>{t('support.ticketSubject')}:</strong> {ticket?.subject}
          </div>
          <div>
            <strong>{t('support.ticketUser')}:</strong> {ticket?.user?.fullName || t('common.noName')} (
            {ticket?.user?.phone})
          </div>
          <div>
            <strong>{t('common.status')}:</strong>{' '}
            <Tag color={statusColors[ticket?.status || 'OPEN']}>
              {statusLabels[ticket?.status || 'OPEN']}
            </Tag>
          </div>
          <div>
            <strong>{t('common.createdAt')}:</strong>{' '}
            {ticket?.createdAt
              ? new Date(ticket.createdAt).toLocaleString('uz-UZ')
              : '-'}
          </div>
        </Space>
      </Card>

      {/* Related Trip Card */}
      {ticket?.tripType && ticket?.tripId && (
        <Card
          title={
            <Space>
              <CarOutlined />
              <span>{t('support.relatedTrip')}</span>
            </Space>
          }
          style={{ marginBottom: 24 }}
          extra={
            <Button type="primary" size="small" onClick={handleViewTrip}>
              To'liq ko'rish
            </Button>
          }
        >
          {ticket.relatedTrip ? (
            <div>
              <Descriptions column={2} size="small">
                <Descriptions.Item label={t('common.id')}>
                  <Tag color="blue">#{ticket.relatedTrip.id}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('common.type')}>
                  <Tag>{tripTypeLabels[ticket.relatedTrip.tripType] || ticket.relatedTrip.tripType}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('common.status')}>
                  <Tag color={tripStatusColors[ticket.relatedTrip.status] || 'default'}>
                    {tripStatusLabels[ticket.relatedTrip.status] || ticket.relatedTrip.status}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('common.price')}>
                  {ticket.relatedTrip.finalPrice
                    ? formatCurrency(ticket.relatedTrip.finalPrice)
                    : formatCurrency(ticket.relatedTrip.priceEstimate)}
                </Descriptions.Item>
              </Descriptions>

              <Divider style={{ margin: '12px 0' }} />

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <EnvironmentOutlined style={{ color: '#1ABC9C', marginTop: 4 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, color: '#7F8C8D' }}>{t('trips.pickupAddress')}</div>
                    <div>{ticket.relatedTrip.pickupAddress}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#7F8C8D' }}>{t('trips.destinationAddress')}</div>
                    <div>{ticket.relatedTrip.destinationAddress}</div>
                  </div>
                </div>
              </div>

              {(ticket.relatedTrip.client || ticket.relatedTrip.driver) && (
                <>
                  <Divider style={{ margin: '12px 0' }} />
                  <Space size="large">
                    {ticket.relatedTrip.client && (
                      <div>
                        <div style={{ fontSize: 12, color: '#7F8C8D' }}>{t('trips.client')}</div>
                        <div>{ticket.relatedTrip.client.fullName || t('common.noName')}</div>
                        <div style={{ fontSize: 12 }}>{ticket.relatedTrip.client.phone}</div>
                      </div>
                    )}
                    {ticket.relatedTrip.driver && (
                      <div>
                        <div style={{ fontSize: 12, color: '#7F8C8D' }}>{t('trips.driver')}</div>
                        <div>{ticket.relatedTrip.driver.fullName || t('common.noName')}</div>
                        <div style={{ fontSize: 12 }}>{ticket.relatedTrip.driver.phone}</div>
                      </div>
                    )}
                  </Space>
                </>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 16, color: '#7F8C8D' }}>
              <Tag color="blue">#{ticket.tripId}</Tag>
              <span style={{ marginLeft: 8 }}>
                {tripTypeLabels[ticket.tripType] || ticket.tripType}
              </span>
              <div style={{ marginTop: 8 }}>
                <Button type="link" onClick={handleViewTrip}>
                  Sayohatni ko'rish
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      <Card title={t('support.messages')} style={{ marginBottom: 24 }}>
        <List
          dataSource={ticket?.messages || []}
          renderItem={(msg: Message) => (
            <List.Item
              style={{
                background:
                  msg.senderType === 'SUPPORT' ? '#E8F8F5' : '#F5F7FA',
                borderRadius: 8,
                padding: 12,
                marginBottom: 8,
              }}
            >
              <List.Item.Meta
                avatar={
                  <Avatar
                    icon={<UserOutlined />}
                    style={{
                      backgroundColor:
                        msg.senderType === 'SUPPORT' ? '#1ABC9C' : '#7F8C8D',
                    }}
                  />
                }
                title={
                  <Space>
                    <span>
                      {msg.sender?.fullName ||
                        (msg.senderType === 'SUPPORT' ? 'Admin' : t('support.ticketUser'))}
                    </span>
                    <span style={{ color: '#7F8C8D', fontSize: 12 }}>
                      {new Date(msg.createdAt).toLocaleString('uz-UZ')}
                    </span>
                  </Space>
                }
                description={msg.content}
              />
            </List.Item>
          )}
        />
      </Card>

      {can('support', 'reply') && ticket?.status !== 'RESOLVED' && ticket?.status !== 'CLOSED' && (
        <Card title={t('support.replySection')}>
          <Input.TextArea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder={t('support.replyPlaceholder')}
            rows={4}
            style={{ marginBottom: 16 }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleReply}
            loading={sending}
          >
            Yuborish
          </Button>
        </Card>
      )}
    </Show>
  );
};
